import React from "react";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import SettingItemLayout from "@/pages/settings/components/settingsItems/SettingItemLayout";
import { cn } from "@/utils/utils";

interface OrgContextItemProps {
  title: string;
  // The stored free-text for this section. Empty/undefined → the section is unset.
  value?: string;
  // Only render the empty state once the backing data has actually loaded. Until then an
  // unset value is indistinguishable from a not-yet-fetched one, so we keep the quiet
  // read-only row (title only) to avoid flashing the CTA during the load/generation window.
  showEmptyState?: boolean;
  notSetLabel: string;
  emptyHint: string;
  ctaLabel: string;
  onAdd: () => void;
  isLastItem?: boolean;
  "data-test"?: string;
}

// A single row of the "Your organization context" section. When the section has a value (or
// its data hasn't loaded yet) it renders read-only via SettingItemLayout. When it is loaded
// and unset it renders the empty state: a "not set up yet" label, a hint, and a CTA that
// opens the drawer focused on this field.
const OrgContextItem: React.FC<OrgContextItemProps> = ({
  title,
  value,
  showEmptyState = true,
  notSetLabel,
  emptyHint,
  ctaLabel,
  onAdd,
  isLastItem = false,
  "data-test": dataTest
}) => {
  // The drawer stores raw textarea content, so a whitespace-only string counts as unset —
  // otherwise it would render a blank populated row with no way to add real content.
  const hasValue = Boolean(value?.trim());

  if (hasValue || !showEmptyState) {
    return (
      <div data-test={dataTest}>
        <SettingItemLayout
          title={title}
          subTitle={hasValue ? value : undefined}
          subTitleBreakAfterSentence
          isLastItem={isLastItem}
        />
      </div>
    );
  }

  return (
    <div
      data-test={dataTest}
      className={cn(
        "w-full flex flex-row items-center justify-between gap-4 p-4",
        !isLastItem && "border-b border-border-border1"
      )}
    >
      <div className="flex flex-col justify-start">
        <Text variant="body">{title}</Text>
        <Text variant="bodySecondary" className="mt-2">
          {notSetLabel}
        </Text>
        <Text variant="bodyTertiary">{emptyHint}</Text>
      </div>
      <Button
        data-test={dataTest ? `${dataTest}-cta` : undefined}
        variant="secondary"
        className="shrink-0"
        onClick={onAdd}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

export default OrgContextItem;
